import { useMemo } from 'react';
import { FormattedDate, FormattedNumber, FormattedTime, useIntl } from 'react-intl';

import { IDriver } from '@/modules/driver/model/driver';
import { DescriptionList } from '@/shared/view/DescriptionList';
import { Tag } from '@/shared/view/Tag';
import { Icon } from '@src-common/view/components/Icon';

import s from './Statistics.module.css';

interface IProps {
  driver: IDriver;
  className?: string;
}

type TTitles = 'acceptance' | 'rating' | 'score' | 'lastActivity' | 'tags';

type TTitleLine = [TTitles, string];

const skipEmpty = (item: TTitleLine | false): item is TTitleLine => item !== false;

const Statistics = ({ className, driver }: IProps) => {
  const intl = useIntl();

  const titles = useMemo((): Array<TTitleLine> => {
    const allTitles: Array<TTitleLine | false> = [
      [
        'acceptance',
        intl.formatMessage({
          defaultMessage: 'Acceptance',
          id: '1vqy76',
          description: 'Driver screen: title',
        }),
      ],
      [
        'rating',
        intl.formatMessage({
          defaultMessage: 'Rating',
          id: 'WBIlW2',
          description: 'Driver screen: title',
        }),
      ],
      [
        'score',
        intl.formatMessage({
          defaultMessage: 'Score',
          id: '/px/5i',
          description: 'Driver screen: title',
        }),
      ],
      [
        'lastActivity',
        intl.formatMessage({
          defaultMessage: 'Last activity',
          id: 'CZ5uHm',
          description: 'Driver screen: title',
        }),
      ],
      [
        'tags',
        intl.formatMessage({
          defaultMessage: 'Tags',
          id: '4Atvju',
          description: 'Driver screen: title',
        }),
      ],
    ];

    return allTitles.filter(skipEmpty);
  }, [intl]);

  const values = useMemo(
    () => ({
      acceptance: () =>
        driver.acceptance && (
          <>
            <FormattedNumber value={driver.acceptance * 100} maximumFractionDigits={0} />%
          </>
        ),
      rating: () => (
        <FormattedNumber
          value={driver.rating}
          maximumFractionDigits={2}
          minimumFractionDigits={2}
        />
      ),
      score: () => (
        <div className={s.score}>
          <Icon icon={'diamond'} className={s.diamond} />
          <FormattedNumber value={driver.score} maximumFractionDigits={0} />
        </div>
      ),
      lastActivity: () => (
        <>
          <FormattedDate value={driver.lastActivity} />{' '}
          <FormattedTime value={driver.lastActivity} />
        </>
      ),
      tags: () => (
        <div className={s.tags}>
          {driver.tags?.map(x => (
            <Tag key={x}>{x}</Tag>
          ))}
        </div>
      ),
    }),
    [driver],
  );

  return (
    <DescriptionList
      title={intl.formatMessage({
        defaultMessage: 'Statistics',
        id: 'mNjCNJ',
        description: 'Driver screen, overview tab: statistics section heading',
      })}
      className={className}
      values={values}
      titles={titles}
    />
  );
};

export { Statistics };
